import { ROUTE_PATTERNS } from '@/shared/config/routes';
import { StaggerItem } from '@/shared/ui/animations';

import {
  AnalysisSectionLayout,
  PROSE_CARD_STAGGER_SLOTS,
  SectionBlocks,
  type SectionBlock,
} from '../../components';
import {
  useAnalysisFromContext,
  type AnalysisSectionProps,
} from '../../utils/useAnalysisFromContext';
import { hasContent } from '../../utils/hasContent';

import {
  BusinessModel,
  CompanyInformation,
  ExecutiveSummary,
  hasBusinessModel,
  hasCompanyInformation,
  hasMarketingMethods,
  KeyInsight,
  MarketingMethodsStrategy,
} from './components';
import { useTranslation } from 'react-i18next';

// The Key Insight card leads the cascade from slot 0, then an empty slot lets the
// callout settle before the sections swipe up in order. `sectionStagger(0)` is the
// first section's index; each later section follows on the next slot.
const CARD_SETTLE_GAP = 1;
const sectionStagger = (order: number) => PROSE_CARD_STAGGER_SLOTS + CARD_SETTLE_GAP + 1 + order;

export const ExecutiveSnapshot = ({
  analysis: analysisProp,
  exporting,
}: AnalysisSectionProps = {}) => {
  const { t } = useTranslation('analysis');
  const title = t('analysisResult.executiveSnapshot.executiveSnapshot');
  const analysis = useAnalysisFromContext(analysisProp);

  const snapshot = analysis.structuredData?.executiveSnapshot;

  const blocks: SectionBlock[] = [
    {
      key: 'key-insight',
      hasData: hasContent(snapshot?.keyInsight),
      node: <KeyInsight text={snapshot?.keyInsight} staggerStart={0} />,
    },
    {
      key: 'summary',
      hasData: hasContent(snapshot?.summary),
      node: (
        <StaggerItem index={sectionStagger(0)} variant="swipeUp">
          <ExecutiveSummary text={snapshot?.summary} />
        </StaggerItem>
      ),
    },
    {
      key: 'company-info',
      hasData: hasCompanyInformation(snapshot?.companyInfo),
      node: (
        <StaggerItem index={sectionStagger(1)} variant="swipeUp">
          <CompanyInformation data={snapshot?.companyInfo} />
        </StaggerItem>
      ),
    },
    {
      key: 'business-model',
      hasData: hasBusinessModel(snapshot?.businessModel),
      node: (
        <StaggerItem index={sectionStagger(2)} variant="swipeUp">
          <BusinessModel data={snapshot?.businessModel} brandName={analysis.brandName} />
        </StaggerItem>
      ),
    },
    {
      key: 'marketing-methods',
      hasData: hasMarketingMethods(snapshot?.marketingMethods),
      node: (
        <StaggerItem index={sectionStagger(3)} variant="swipeUp">
          <MarketingMethodsStrategy data={snapshot?.marketingMethods} />
        </StaggerItem>
      ),
    },
  ];

  return (
    <AnalysisSectionLayout
      analysisId={analysis.id}
      brand={analysis.brandName}
      section={ROUTE_PATTERNS.IQ_SECTION_EXECUTIVE_SNAPSHOT}
      title={title}
      subtitle={t('analysisResult.executiveSnapshot.brandAnalysisOverview', {
        brandName: analysis.brandName,
      })}
      exporting={exporting}
    >
      <SectionBlocks category={title} blocks={blocks} />
    </AnalysisSectionLayout>
  );
};
